import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import clsx from 'clsx';
import type { VerifyLine } from '@1qa/shared';
import { CheckCircle2, ChevronDown, CircleAlert, Download, Gauge, Info, XCircle } from '@/components/icons';
import type { PerfStepTotal, PerfSummaryView, Run, TestResult } from '@/lib/types';
import { describeModel, describeSlo, formatMinutes, formatMs, formatNumber, formatPct, VERDICT } from '@/lib/perf';
import { formatDateTime } from '@/lib/format';
import { errorsOf, explainError, runSuggestions, SIDE_LABEL, type Suggestion } from '@/lib/perfErrors';
import { Badge, Button, Card, CardBody, CardHeader, EmptyState } from '@/components/ui';
import { TimeChart } from './TimeChart';

/**
 * Printing (Export as PDF, the run page's Print, Ctrl+P) shows the whole report: closed parts open just before the
 * browser prints, and close again afterwards.
 */
function useOpenWhilePrinting(setOpen: (open: boolean) => void, open: boolean) {
  const was = useRef(open);
  was.current = open;
  useEffect(() => {
    let before = false;
    const on = () => {
      before = was.current;
      flushSync(() => setOpen(true));
    };
    const off = () => setOpen(before);
    window.addEventListener('beforeprint', on);
    window.addEventListener('afterprint', off);
    return () => {
      window.removeEventListener('beforeprint', on);
      window.removeEventListener('afterprint', off);
    };
  }, [setOpen]);
}

/** Same for <details> sections inside `root`. */
function useOpenDetailsWhilePrinting(root: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    let closed: HTMLDetailsElement[] = [];
    const on = () => {
      closed = [...(root.current?.querySelectorAll('details') ?? [])].filter((d) => !d.open);
      for (const d of closed) d.open = true;
    };
    const off = () => {
      for (const d of closed) d.open = false;
      closed = [];
    };
    window.addEventListener('beforeprint', on);
    window.addEventListener('afterprint', off);
    return () => {
      window.removeEventListener('beforeprint', on);
      window.removeEventListener('afterprint', off);
    };
  }, [root]);
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="min-w-0 rounded-card bg-surface-sunken px-4 py-3 ring-1 ring-border-subtle ring-inset">
      <div className="text-xs font-medium tracking-label text-fg-muted uppercase">{label}</div>
      <div className="mt-1 text-xl font-semibold text-fg tabular-nums">{value}</div>
      {hint && <div className="mt-0.5 truncate text-xs text-fg-muted">{hint}</div>}
    </div>
  );
}

/** Numbers of the run: the steady part when there is one (that is what the goals are judged on). */
export function PerfStats({ s }: { s: PerfSummaryView }) {
  const t = s.totals.steady?.all ?? s.totals.all;
  const seconds = (new Date(s.provenance.endedAt).getTime() - new Date(s.provenance.startAt).getTime()) / 1000;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <Stat label="Requests" value={formatNumber(s.totals.all.requests)} hint={`${formatNumber(s.totals.iterations)} journeys`} />
      <Stat label="Per second" value={formatNumber(t.rps)} hint={s.totals.steady ? 'steady part' : 'whole run'} />
      <Stat label="p95" value={formatMs(t.p95Ms)} hint={`median ${formatMs(t.p50Ms)}`} />
      <Stat label="p99" value={formatMs(t.p99Ms)} hint={`slowest ${formatMs(t.maxMs)}`} />
      <Stat label="Errors" value={formatPct(t.errorRate)} hint={`${formatNumber(t.failed)} failed`} />
      <Stat label="Users" value={formatNumber(s.totals.peakVus)} hint={`${formatMinutes(Math.max(0, seconds))} in all`} />
    </div>
  );
}

/** The verdict and why, in a coloured box. */
export function VerdictBanner({ s }: { s: PerfSummaryView }) {
  const v = VERDICT[s.verdict];
  const Icon = s.verdict === 'PASS' ? CheckCircle2 : s.verdict === 'FAIL' ? XCircle : s.verdict === 'INVALID' ? CircleAlert : Info;
  return (
    <div
      role="status"
      className={clsx(
        'flex items-start gap-3 rounded-card p-4 ring-1 ring-inset',
        s.verdict === 'PASS' && 'bg-success-subtle ring-success/40',
        s.verdict === 'FAIL' && 'bg-danger-subtle ring-danger-line',
        s.verdict === 'INVALID' && 'bg-warning-subtle ring-warning/40',
        s.verdict === 'DEGRADED' && 'bg-info-subtle ring-border',
      )}
      data-testid="perf-verdict"
    >
      <Icon className={clsx('mt-0.5 size-5 shrink-0', s.verdict === 'PASS' ? 'text-success' : s.verdict === 'FAIL' ? 'text-danger-text' : s.verdict === 'INVALID' ? 'text-warning' : 'text-link')} aria-hidden />
      <div className="min-w-0 text-sm">
        <div className="font-semibold text-fg">
          {s.config.verify ? (s.verdict === 'PASS' ? 'Check run passed: the script works' : 'Check run failed') : v.label}
        </div>
        <p className="mt-0.5 text-fg-secondary">{s.reason ?? (s.config.verify ? 'Every step answered as recorded. The test is ready for load.' : v.text)}</p>
        {s.config.aboveEstimate && <p className="mt-1 text-xs text-fg-muted">Started above what the chosen load agents were estimated to carry ({formatNumber(s.config.estimateVus ?? 0)} users), by a Project Admin.</p>}
      </div>
    </div>
  );
}

function GoalsTable({ s }: { s: PerfSummaryView }) {
  if (!s.slos.length) return null;
  return (
    <Card>
      <CardHeader title="Goals" description={s.provenance.steadyWindow ? 'Judged on the steady part of the run, after the ramp-up.' : 'Judged on the whole run.'} />
      <ul className="divide-y divide-border-subtle">
        {s.slos.map((r, i) => (
          <li key={i} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
            {r.pass ? <CheckCircle2 className="size-4 text-success" aria-hidden /> : <XCircle className="size-4 text-danger-text" aria-hidden />}
            <span className="min-w-0 flex-1 text-fg">{describeSlo(r.slo)}</span>
            <span className="text-fg-secondary tabular-nums">{r.value === null ? 'no requests' : r.slo.metric === 'errorRate' ? formatPct(r.value) : formatMs(r.value)}</span>
            <Badge tone={r.pass ? 'success' : 'danger'}>{r.pass ? 'Met' : 'Missed'}</Badge>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Charts({ s }: { s: PerfSummaryView }) {
  const tl = s.totals.timeline;
  if (tl.length < 2) return null;
  const t0 = tl[0]!.start;
  const steady = s.provenance.steadyWindow
    ? { from: Math.max(0, (new Date(s.provenance.steadyWindow.from).getTime() - t0) / 1000), to: Math.max(0, (new Date(s.provenance.steadyWindow.to).getTime() - t0) / 1000), label: 'Steady part (goals judged here)' }
    : null;
  const p95Goal = s.config.slos.find((x) => x.metric === 'p95' && x.scope === 'all');
  return (
    <Card>
      <CardHeader title="Over time" description="Every point is 5 seconds of the run; the shaded part is where the goals are judged." />
      <CardBody className="grid gap-6 lg:grid-cols-2">
        <TimeChart
          title="Response time"
          series={[
            { label: 'median', color: 'text-fg-muted', values: tl.map((p) => p.p50Ms) },
            { label: 'p95', color: 'text-primary', values: tl.map((p) => p.p95Ms) },
            { label: 'p99', color: 'text-warning', values: tl.map((p) => p.p99Ms), dashed: true },
          ]}
          format={(v) => formatMs(v)}
          goal={p95Goal ? { value: p95Goal.max, label: `p95 goal ${formatMs(p95Goal.max)}` } : undefined}
          shade={steady}
        />
        <TimeChart title="Requests per second" series={[{ label: 'requests / s', color: 'text-primary', values: tl.map((p) => p.rps) }]} format={(v) => formatNumber(v)} shade={steady} />
        <TimeChart title="Users" series={[{ label: 'users', color: 'text-success', values: tl.map((p) => p.vus) }]} format={(v) => formatNumber(v)} shade={steady} />
        <TimeChart title="Errors" series={[{ label: 'failed requests', color: 'text-danger-text', values: tl.map((p) => p.errorRate * 100) }]} format={(v) => `${Math.round(v * 10) / 10} %`} shade={steady} />
      </CardBody>
    </Card>
  );
}

/** Requests (or a group of them) added up: counts and errors summed; times are the slowest request's of the group. */
function rollUp(rows: PerfStepTotal[]): PerfStepTotal {
  const requests = rows.reduce((n, r) => n + r.requests, 0);
  const failed = rows.reduce((n, r) => n + r.failed, 0);
  const max = (k: 'p50Ms' | 'p95Ms' | 'p99Ms' | 'maxMs') => rows.reduce<number | null>((m, r) => (r[k] === null ? m : Math.max(m ?? 0, r[k]!)), null);
  const errors: Record<string, number> = {};
  for (const r of rows) for (const [k, n] of Object.entries(r.errors)) errors[k] = (errors[k] ?? 0) + n;
  return {
    ...rows[0]!,
    requests,
    failed,
    errorRate: requests ? failed / requests : 0,
    errors,
    rps: rows.reduce((n, r) => n + r.rps, 0),
    p50Ms: max('p50Ms'),
    p95Ms: max('p95Ms'),
    p99Ms: max('p99Ms'),
    maxMs: max('maxMs'),
  };
}

const COLS = 'perf-cols grid grid-cols-[minmax(14rem,1fr)_repeat(7,5.25rem)] items-center gap-x-1';

function Numbers({ r, strong }: { r: PerfStepTotal; strong?: boolean }) {
  return (
    <>
      <span className="text-right tabular-nums">{formatNumber(r.requests)}</span>
      <span className="text-right tabular-nums">{formatNumber(r.rps)}</span>
      <span className="text-right tabular-nums">{formatMs(r.p50Ms)}</span>
      <span className={clsx('text-right tabular-nums', strong !== false && 'font-medium')}>{formatMs(r.p95Ms)}</span>
      <span className="text-right tabular-nums">{formatMs(r.p99Ms)}</span>
      <span className="text-right tabular-nums">{formatMs(r.maxMs)}</span>
      <span className={clsx('text-right tabular-nums', r.failed > 0 && 'font-medium text-danger-text')} title={Object.entries(r.errors).map(([k, n]) => `${explainError(k).title}: ${n}`).join(', ') || undefined}>
        {formatPct(r.errorRate)}
      </span>
    </>
  );
}

/** The run's scenarios, each with its actions (transactions) and their requests, as collapsible rows. */
function ScenarioResults({ s, rows }: { s: PerfSummaryView; rows: PerfStepTotal[] }) {
  const scenarios = s.config.scenarios ?? [];
  const multi = scenarios.length > 1;
  // "Scenario › Action" when the run had several scenarios; otherwise the action alone.
  const groups = new Map<string, Map<string, PerfStepTotal[]>>();
  for (const r of rows) {
    const sc = multi ? (scenarios.find((x) => r.transaction.startsWith(`${x.name} \u203a `))?.name ?? 'Other') : (scenarios[0]?.name ?? 'Journey');
    const action = multi && r.transaction.startsWith(`${sc} \u203a `) ? r.transaction.slice(sc.length + 3) : r.transaction;
    const acts = groups.get(sc) ?? new Map<string, PerfStepTotal[]>();
    acts.set(action, [...(acts.get(action) ?? []), r]);
    groups.set(sc, acts);
  }
  const order = [...scenarios.map((x) => x.name).filter((n) => groups.has(n)), ...[...groups.keys()].filter((n) => !scenarios.some((x) => x.name === n))];
  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Results by scenario"
        description="Each scenario, its actions and their requests. Open a row to see what is inside; scenario and action rows add up the requests (times: the slowest request’s)."
      />
      <div className="overflow-x-auto">
        <div className="min-w-[760px] text-sm" data-testid="perf-scenarios">
          <div className={clsx(COLS, 'bg-surface-sunken px-5 py-2 text-xs font-medium tracking-label text-fg-muted uppercase')}>
            <span>Scenario / action / request</span>
            {['Count', 'Per s', 'Median', 'p95', 'p99', 'Slowest', 'Errors'].map((h) => (
              <span key={h} className="text-right">
                {h}
              </span>
            ))}
          </div>
          {order.map((sc) => {
            const acts = groups.get(sc)!;
            const all = rollUp([...acts.values()].flat());
            return (
              <details key={sc} open={order.length === 1} className="group/sc border-t border-border">
                <summary className={clsx(COLS, 'cursor-pointer list-none px-5 py-2.5 font-semibold text-fg hover:bg-surface-sunken/60 [&::-webkit-details-marker]:hidden')}>
                  <span className="flex min-w-0 items-center gap-2">
                    <ChevronDown className="size-3.5 shrink-0 -rotate-90 text-fg-muted transition group-open/sc:rotate-0" aria-hidden />
                    <span className="truncate">{sc}</span>
                    <span className="shrink-0 text-xs font-normal text-fg-muted">
                      {acts.size} action{acts.size === 1 ? '' : 's'}
                    </span>
                  </span>
                  <Numbers r={all} />
                </summary>
                {[...acts.entries()].map(([action, list]) => (
                  <details key={action} className="group/tx border-t border-border-subtle">
                    <summary className={clsx(COLS, 'cursor-pointer list-none py-2 pr-5 pl-10 text-fg hover:bg-surface-sunken/60 [&::-webkit-details-marker]:hidden')}>
                      <span className="flex min-w-0 items-center gap-2">
                        <ChevronDown className="size-3.5 shrink-0 -rotate-90 text-fg-muted transition group-open/tx:rotate-0" aria-hidden />
                        <span className="truncate font-medium" title={action}>
                          {action}
                        </span>
                        <span className="shrink-0 text-xs text-fg-muted">
                          {list.length} request{list.length === 1 ? '' : 's'}
                        </span>
                      </span>
                      <Numbers r={rollUp(list)} />
                    </summary>
                    <div className="divide-y divide-border-subtle bg-surface-sunken/40">
                      {list.map((r) => (
                        <div key={r.step} className={clsx(COLS, 'py-1.5 pr-5 pl-16 text-fg-secondary')}>
                          <span className="truncate font-mono text-xs" title={r.step}>
                            {r.step}
                          </span>
                          <Numbers r={r} strong={false} />
                        </div>
                      ))}
                    </div>
                  </details>
                ))}
              </details>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

const SUGGESTION_STYLE: Record<Suggestion['tone'], { icon: typeof Info; className: string }> = {
  danger: { icon: XCircle, className: 'text-danger-text' },
  warning: { icon: CircleAlert, className: 'text-warning' },
  info: { icon: Info, className: 'text-link' },
  success: { icon: CheckCircle2, className: 'text-success' },
};

/** What the run says and what to do next, worked out from its errors and numbers (lib/perfErrors.ts). */
function Suggestions({ s }: { s: PerfSummaryView }) {
  const list = runSuggestions(s);
  if (!list.length) return null;
  return (
    <Card>
      <CardHeader title="Findings and suggestions" description="Worked out from this run’s errors and numbers. A starting point for the team, not a diagnosis: confirm with the app’s logs and monitoring." />
      <div className="divide-y divide-border-subtle text-sm" data-testid="perf-suggestions">
        {list.map((x, i) => {
          const { icon: Icon, className } = SUGGESTION_STYLE[x.tone];
          return (
            <details key={i} className="group">
              <summary className="flex cursor-pointer list-none items-center gap-3 px-5 py-3 hover:bg-surface-sunken/60 [&::-webkit-details-marker]:hidden">
                <Icon className={clsx('size-4 shrink-0', className)} aria-hidden />
                <span className="min-w-0 flex-1 font-medium text-fg">{x.title}</span>
                <ChevronDown className="size-4 shrink-0 text-fg-muted transition group-open:rotate-180" aria-hidden />
              </summary>
              <p className="px-5 pb-3 pl-12 text-fg-secondary">{x.text}</p>
            </details>
          );
        })}
      </div>
    </Card>
  );
}

/** Every kind of error in words: what it means, what to look at, who most likely acts, and the requests that had it. */
function Errors({ rows }: { rows: PerfStepTotal[] }) {
  const list = errorsOf(rows);
  if (!list.length) return null;
  return (
    <Card>
      <CardHeader title="Errors" description="What went wrong, what it means and where to look." />
      <div className="divide-y divide-border-subtle text-sm">
        {list.map(({ info, count, steps }) => (
          <details key={info.key} className="group">
            <summary className="flex cursor-pointer list-none flex-wrap items-center gap-3 px-5 py-3 hover:bg-surface-sunken/60 [&::-webkit-details-marker]:hidden">
              <ChevronDown className="size-4 shrink-0 -rotate-90 text-fg-muted transition group-open:rotate-0" aria-hidden />
              <span className="font-medium text-fg">{info.title}</span>
              <Badge tone={info.side === 'app' ? 'danger' : 'warning'}>{SIDE_LABEL[info.side]}</Badge>
              <span className="ml-auto font-mono text-xs text-fg-muted" title="Error class, for searching logs">
                {info.key}
              </span>
              <span className="w-20 text-right font-medium tabular-nums">{formatNumber(count)}</span>
            </summary>
            <div className="space-y-1 px-5 pb-3 pl-12">
              <p className="text-fg-secondary">{info.meaning}</p>
              <p className="text-fg-secondary">
                <span className="font-medium text-fg">What to do: </span>
                {info.advice}
              </p>
              {steps.length > 0 && <p className="text-xs text-fg-muted">Most in: {steps.map((x) => `${x.name} (${formatNumber(x.count)})`).join(', ')}</p>}
            </div>
          </details>
        ))}
      </div>
    </Card>
  );
}

function VerifySteps({ s }: { s: PerfSummaryView }) {
  return (
    <Card>
      <CardHeader title="Steps of the check run" description="One user went through the journey once; every step should answer as recorded." />
      <VerifyLines lines={s.totals.verify ?? []} />
    </Card>
  );
}

/** JSON shown indented; anything else as it came. */
function pretty(body: string): string {
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
}

function HeaderTable({ headers }: { headers: { name: string; value: string }[] }) {
  if (!headers.length) return <p className="text-fg-muted">No headers</p>;
  return (
    <dl className="grid grid-cols-[minmax(6rem,auto)_1fr] gap-x-3 gap-y-0.5 font-mono">
      {headers.map((h, i) => (
        <div key={i} className="contents">
          <dt className="text-fg-muted">{h.name}</dt>
          <dd className="break-all text-fg">{h.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Body({ text }: { text?: string }) {
  if (!text) return <p className="text-fg-muted">No body</p>;
  return <pre className="max-h-72 overflow-auto rounded-field bg-surface-sunken p-2 font-mono whitespace-pre-wrap break-all text-fg">{pretty(text)}</pre>;
}

/** One request of a check run; its request / response details open on demand (failed ones open by themselves). */
function VerifyLineItem({ l }: { l: VerifyLine }) {
  const details = Boolean(l.request || l.response);
  const [open, setOpen] = useState(!l.ok && details);
  useOpenWhilePrinting((o) => setOpen(o && details), open);
  return (
    <li className="px-5 py-2.5">
      <div className="flex flex-wrap items-center gap-3">
        {l.ok ? <CheckCircle2 className="size-4 text-success" aria-hidden /> : <XCircle className="size-4 text-danger-text" aria-hidden />}
        <span className="font-mono text-xs text-fg">{l.name}</span>
        <span className="text-xs text-fg-muted">{l.tx}</span>
        <span className="ml-auto flex items-center gap-3">
          {l.status !== undefined && <span className="text-xs text-fg-secondary tabular-nums">answered {l.status}</span>}
          {l.response?.timeMs !== undefined && <span className="text-xs text-fg-muted tabular-nums">{formatMs(l.response.timeMs)}</span>}
          {details && (
            <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-xs text-link hover:underline">
              Details <ChevronDown className={clsx('size-3 transition', open && 'rotate-180')} aria-hidden />
            </button>
          )}
        </span>
      </div>
      {!l.ok && (
        <div className="mt-1 ml-7 space-y-1 text-xs text-fg-secondary">
          {l.why && <p>{l.why}</p>}
          {l.error && <p>{l.error}</p>}
          {!!l.missing?.length && <p>Found no value for: {l.missing.join(', ')} (the correlation rule matched nothing).</p>}
          {l.failedChecks?.map((c) => <p key={c}>Check failed: {c}</p>)}
          {l.body && !open && <pre className="max-h-32 overflow-auto rounded-field bg-surface-sunken p-2 font-mono whitespace-pre-wrap">{l.body}</pre>}
        </div>
      )}
      {open && (
        <div className="mt-3 ml-7 space-y-3 text-xs">
          {!!l.taken?.length && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-fg-muted">Values taken from the response:</span>
              {l.taken.map((t) => (
                <code key={t.var} className={clsx('rounded px-1.5 py-0.5 font-mono', t.found ? 'bg-primary-subtle text-primary' : 'bg-danger-subtle text-danger-text')}>
                  {t.var} {t.found ? `= ${t.value ?? ''}` : 'not found'}
                </code>
              ))}
            </div>
          )}
          <div className="grid gap-3 lg:grid-cols-2">
            {l.request && (
              <section aria-label="Request" className="min-w-0 space-y-2 rounded-card p-3 ring-1 ring-border-subtle ring-inset">
                <h4 className="font-semibold tracking-label text-fg-muted uppercase">Request</h4>
                <p className="font-mono break-all text-fg">
                  <span className="font-semibold">{l.request.method}</span> {l.request.url}
                </p>
                <HeaderTable headers={l.request.headers} />
                <Body text={l.request.body} />
              </section>
            )}
            {l.response && (
              <section aria-label="Response" className="min-w-0 space-y-2 rounded-card p-3 ring-1 ring-border-subtle ring-inset">
                <h4 className="font-semibold tracking-label text-fg-muted uppercase">
                  Response{l.response.status !== undefined ? ` · ${l.response.status}` : ''}
                  {l.response.timeMs !== undefined ? ` · ${formatMs(l.response.timeMs)}` : ''}
                </h4>
                <HeaderTable headers={l.response.headers} />
                <Body text={l.response.body} />
              </section>
            )}
          </div>
          <p className="text-fg-muted">Secret test data is shown as ••••••. Long values are cut.</p>
        </div>
      )}
    </li>
  );
}

/** A check run's requests in order: answered as recorded or not, why, and each request and response (also the script page's checks). */
export function VerifyLines({ lines }: { lines: VerifyLine[] }) {
  return (
    <ul className="divide-y divide-border-subtle text-sm">
      {lines.map((l, i) => (
        <VerifyLineItem key={i} l={l} />
      ))}
    </ul>
  );
}

function AgentsAndDetails({ s }: { s: PerfSummaryView }) {
  const [open, setOpen] = useState(false);
  useOpenWhilePrinting(setOpen, open);
  return (
    <Card>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex w-full items-center gap-3 px-5 py-4 text-left">
        <span className="min-w-0 flex-1">
          <span className="block text-base font-semibold text-fg">Load agents and run details</span>
          <span className="block text-sm text-fg-secondary">
            {s.config.scenarios && s.config.scenarios.length > 1 ? `${s.config.scenarios.length} scenarios · ` : ''}
            {s.totals.agents.length} agent{s.totals.agents.length === 1 ? '' : 's'} · {describeModel(s.config.model)}
            {s.provenance.collection ? ` · ${s.provenance.collection} test data` : ''}
          </span>
        </span>
        <ChevronDown className={clsx('size-4 text-fg-muted transition', open && 'rotate-180')} aria-hidden />
      </button>
      {open && (
        <CardBody className="space-y-4 border-t border-border-subtle text-sm">
          <ul className="space-y-2">
            {s.totals.agents.map((a) => {
              const prov = s.provenance.agents.find((p) => p.id === a.id);
              return (
                <li key={a.id} className="flex flex-wrap items-center gap-3">
                  <Gauge className="size-4 text-fg-muted" aria-hidden />
                  <span className="font-medium text-fg">{a.name}</span>
                  <Badge tone={a.status === 'done' ? 'success' : 'warning'}>{a.status === 'done' ? 'Finished' : a.status === 'lost' ? 'Lost' : 'Failed'}</Badge>
                  <span className="text-xs text-fg-secondary">
                    CPU up to {a.maxCores.toFixed(1)} of {a.machineCores || '?'} cores · memory up to {formatNumber(a.maxRssMb)} MB
                    {prov?.segment ? ` · share ${prov.segment}` : ''}
                    {prov?.engineVersion ? ` · k6 ${prov.engineVersion}` : ''}
                  </span>
                  {a.error && <span className="w-full pl-7 text-xs text-warning">{a.error}</span>}
                </li>
              );
            })}
          </ul>
          <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-[max-content_1fr]">
            <dt className="text-fg-muted">Started</dt>
            <dd className="text-fg">{formatDateTime(s.provenance.startAt)}</dd>
            {s.provenance.steadyWindow && (
              <>
                <dt className="text-fg-muted">Steady part</dt>
                <dd className="text-fg">
                  {formatDateTime(s.provenance.steadyWindow.from)} to {formatDateTime(s.provenance.steadyWindow.to)}
                </dd>
              </>
            )}
            <dt className="text-fg-muted">Hosts it could reach</dt>
            <dd className="font-mono text-fg">{s.provenance.allowedHosts.join(', ') || '-'}</dd>
            {s.config.scenarios?.length ? (
              <>
                <dt className="text-fg-muted">Scenarios</dt>
                <dd className="space-y-0.5 text-fg" data-testid="report-scenarios">
                  {s.config.scenarios.map((sc) => {
                    const sha = s.provenance.scripts?.find((x) => x.scenario === sc.name)?.sha256;
                    return (
                      <div key={sc.id}>
                        <span className="font-medium">{sc.name}</span>: script “{sc.scriptName}”, {describeModel(sc.model).replace(/^Up to /, 'up to ')}
                        {sc.startAfterS ? `, from ${formatMinutes(sc.startAfterS)} on` : ''}
                        {sc.dataSet ? `, test data “${sc.dataSet.name}” (${formatNumber(sc.dataSet.rows)} rows, ${sc.dataSet.whenUsedUp === 'stop' ? 'each used once' : 'starting again when used'})` : ''}
                        {sha && (
                          <span className="ml-1 font-mono text-fg-muted" title="SHA-256 of the script that ran: compare runs of the same script">
                            ({sha.slice(0, 12)})
                          </span>
                        )}
                      </div>
                    );
                  })}
                </dd>
              </>
            ) : (
              s.provenance.scriptSha256 && (
                <>
                  <dt className="text-fg-muted">Script fingerprint</dt>
                  <dd className="font-mono text-fg" title="SHA-256 of the script that ran: compare runs of the same script">
                    {s.provenance.scriptSha256.slice(0, 16)}
                  </dd>
                </>
              )
            )}
            {s.totals.profile?.iterationSeconds && (
              <>
                <dt className="text-fg-muted">One journey</dt>
                <dd className="text-fg">
                  {s.totals.profile.iterationSeconds} s, {s.totals.profile.requestsPerIteration} requests, about {formatNumber(s.totals.profile.bytesPerRequest / 1024)} KB per request
                </dd>
              </>
            )}
          </dl>
        </CardBody>
      )}
    </Card>
  );
}

/** The report of a finished performance run. */
export function PerfReport({ run, result, title }: { run: Run; result: TestResult | undefined; title?: string }) {
  const s = result?.perfSummary;
  const root = useRef<HTMLDivElement>(null);
  useOpenDetailsWhilePrinting(root);
  // The PDF is the browser's print to PDF of the whole report, every part open; its file name comes from the page title.
  const exportPdf = () => {
    const before = document.title;
    const day = (run.finishedAt ?? run.queuedAt).slice(0, 10);
    document.title = `${title ?? result?.testCase?.name ?? 'Performance test'} - performance report ${day}`;
    window.print();
    document.title = before;
  };
  if (!s) {
    return (
      <Card>
        <EmptyState
          icon={<Gauge className="size-6" />}
          title={run.status === 'CANCELLED' ? 'Stopped before it started' : 'No measurements'}
          description={run.error ?? result?.error ?? 'This run ended before any load was measured.'}
        />
      </Card>
    );
  }
  return (
    <div ref={root} className="space-y-6" data-testid="perf-report">
      <div className="no-print flex justify-end">
        <Button variant="outline" size="sm" onClick={exportPdf} icon={<Download className="size-4" />}>
          Export as PDF
        </Button>
      </div>
      <VerdictBanner s={s} />
      {s.config.verify ? (
        <VerifySteps s={s} />
      ) : (
        <>
          <PerfStats s={s} />
          <Suggestions s={s} />
          <GoalsTable s={s} />
          <Charts s={s} />
          <ScenarioResults s={s} rows={s.totals.steady?.steps.length ? s.totals.steady.steps : s.totals.steps} />
          <Errors rows={s.totals.steps} />
        </>
      )}
      <AgentsAndDetails s={s} />
    </div>
  );
}
